// src/hooks/useFetchViewAnswers.js
import { useEffect, useState } from "react";
import { auth, db } from "../services/firebase";
import { ref, get } from "firebase/database";

/**
 * 🧠 useFetchViewAnswers
 * Fetches user's answers, evaluations, and question details,
 * displaying questions in the same order (orderId) saved under /Tests/Answers.
 */
export default function useFetchViewAnswers(domainId, packId, interviewId) {
    const [data, setData] = useState({
        questions: [],
        answers: {},
        evaluations: {},
        overallScore: 0,
        totalQuestions: 0,
    });

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const userId = auth.currentUser?.uid;

    useEffect(() => {
        const fetchAll = async () => {
            try {
                if (!userId || !domainId || !packId || !interviewId) return;

                // ✅ Fetch user's answers (with orderId)
                const answersSnap = await get(
                    ref(db, `Tests/Answers/${userId}/${domainId}/${packId}/${interviewId}`)
                );
                const answersData = answersSnap.val() || {};

                // ✅ Fetch evaluations
                const evalSnap = await get(
                    ref(db, `Tests/Evaluations/${userId}/${domainId}/${packId}/${interviewId}`)
                );
                const evalData = evalSnap.val() || {};

                // ✅ Collect all question IDs with their saved orderId
                const orderedIds = Object.entries(answersData)
                    .map(([qId, value]) => ({
                        id: qId,
                        orderId: value?.orderId ?? 0,
                    }))
                    .sort((a, b) => (a.orderId ?? 0) - (b.orderId ?? 0));

                // ✅ Fetch question details in the same order
                const fetchedQuestions = [];
                for (const { id, orderId } of orderedIds) {
                    const qSnap = await get(ref(db, `Questions/AllQuestions/${domainId}/${id}`));
                    if (qSnap.exists()) {
                        fetchedQuestions.push({
                            id,
                            orderId, // ✅ from user's saved answer
                            ...qSnap.val(),
                        });
                    }
                }

                // ✅ Calculate overall score
                const totalScore = Object.values(evalData).reduce(
                    (sum, e) => sum + (e?.score ?? 0),
                    0
                );
                const maxScore = orderedIds.length * 10;
                const overallScore =
                    orderedIds.length > 0
                        ? ((totalScore / maxScore) * 100).toFixed(1)
                        : 0;

                // ✅ Final data
                setData({
                    questions: fetchedQuestions,
                    answers: answersData,
                    evaluations: evalData,
                    overallScore,
                    totalQuestions: orderedIds.length,
                });
            } catch (err) {
                console.error("❌ useFetchViewAnswers error:", err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchAll();
    }, [userId, domainId, packId, interviewId]);

    return { ...data, loading, error };
}
